import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { eur } from "../lib/format";
import * as api from "../api";

const TYPE_LABEL = {
  appartamento: "Appartamento", casa_vacanze: "Casa vacanze", bb: "B&B", affittacamere: "Affittacamere",
  hotel: "Hotel", residence: "Residence", agriturismo: "Agriturismo", villaggio: "Villaggio", altro: "Altro",
};

function AdminAccDetail({ accId, onClose }) {
  const qc = useQueryClient();
  const { data: acc, isLoading } = useQuery({ queryKey: ["adminAccDetail", accId], queryFn: () => api.adminGetAccommodation(accId) });
  const setStatus = useMutation({
    mutationFn: (status) => api.adminSetAccommodationStatus(accId, status),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["adminAcc"] }); qc.invalidateQueries({ queryKey: ["adminAccDetail", accId] }); toast.success(`Struttura → ${r.status}`); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const archive = useMutation({
    mutationFn: () => api.adminArchiveAccommodation(accId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminAcc"] }); qc.invalidateQueries({ queryKey: ["adminAccDetail", accId] }); toast.success("Struttura archiviata"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const restore = useMutation({
    mutationFn: () => api.adminRestoreAccommodation(accId),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["adminAcc"] }); qc.invalidateQueries({ queryKey: ["adminAccDetail", accId] }); toast.success(`Struttura ripristinata → ${r.status}`); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const [nu, setNu] = useState({ name: "", quantity: 1, capacity_total: 2, base_price: 0 });
  const invalDetail = () => { qc.invalidateQueries({ queryKey: ["adminAcc"] }); qc.invalidateQueries({ queryKey: ["adminAccDetail", accId] }); };
  const addUnit = useMutation({
    mutationFn: () => api.adminCreateUnit(accId, { name: nu.name, quantity: Number(nu.quantity), capacity_adults: Number(nu.capacity_total), capacity_children: 0, capacity_total: Number(nu.capacity_total), base_price: Number(nu.base_price), min_stay: 1 }),
    onSuccess: () => { setNu({ name: "", quantity: 1, capacity_total: 2, base_price: 0 }); invalDetail(); toast.success("Unità aggiunta"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const delUnit = useMutation({
    mutationFn: (uid) => api.adminDeleteUnit(uid),
    onSuccess: (r) => { invalDetail(); toast.success(r.deactivated ? "Unità disattivata (storico preservato)" : "Unità eliminata"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-acc-detail-modal">
        {isLoading || !acc ? <p className="text-slate-400">Caricamento...</p> : (
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading font-semibold text-xl text-slate-900">{acc.name}</h3>
                <p className="text-sm text-slate-400">{TYPE_LABEL[acc.type]} · {acc.city} ({acc.province}) · {acc.destination}</p>
                <p className="text-xs text-slate-400 mt-1">Operatore: <b>{acc.operator?.legal_name || acc.operator?.name || acc.operator_id}</b> · {acc.operator?.email}</p>
              </div>
              <button onClick={onClose} className="text-slate-400 text-2xl leading-none">×</button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-bold rounded-full px-3 py-1 ${acc.status === "ACTIVE" ? "bg-emerald-50 text-emerald-600" : acc.status === "SUSPENDED" ? "bg-red-50 text-red-500" : acc.status === "ARCHIVED" ? "bg-slate-200 text-slate-600" : "bg-amber-50 text-amber-600"}`}>{acc.status}{acc.status === "ARCHIVED" ? " · ARCHIVIATA" : ""}</span>
              <div className="flex-1" />
              {acc.status === "ARCHIVED" ? (
                <button data-testid="admin-acc-restore" onClick={() => restore.mutate()} className="rounded-full bg-primary text-white text-xs font-semibold px-4 py-2">Ripristina</button>
              ) : (
                <>
                  {acc.status !== "ACTIVE" && <button data-testid="admin-acc-activate" onClick={() => setStatus.mutate("ACTIVE")} className="rounded-full bg-emerald-600 text-white text-xs font-semibold px-4 py-2">Attiva</button>}
                  {acc.status !== "SUSPENDED" && <button data-testid="admin-acc-suspend" onClick={() => setStatus.mutate("SUSPENDED")} className="rounded-full bg-red-500 text-white text-xs font-semibold px-4 py-2">Disattiva</button>}
                  <button data-testid="admin-acc-archive" onClick={() => archive.mutate()} className="rounded-full bg-slate-600 text-white text-xs font-semibold px-4 py-2">Archivia</button>
                </>
              )}
            </div>
            {acc.cin && <p className="text-xs text-slate-500">CIN: <b>{acc.cin}</b>{acc.cir ? ` · CIR: ${acc.cir}` : ""}</p>}

            <div>
              <h4 className="font-semibold text-slate-800 text-sm mb-2">Unità ({acc.units?.length || 0})</h4>
              <div className="space-y-1">
                {(acc.units || []).map((u) => (
                  <div key={u.id} className="flex justify-between items-center text-sm rounded-lg border border-slate-100 px-3 py-2" data-testid={`admin-acc-unit-${u.id}`}>
                    <span>{u.name} <span className="text-xs text-slate-400">· qtà {u.quantity} · {u.capacity_total} ospiti</span></span>
                    <span className="flex items-center gap-2"><span className="font-semibold text-primary">{eur(u.base_price)}/notte</span>
                      <button data-testid={`admin-acc-unit-del-${u.id}`} onClick={() => { if (window.confirm("Eliminare l'unità? Se ha prenotazioni verrà disattivata.")) delUnit.mutate(u.id); }} className="text-red-400 text-lg leading-none">×</button>
                    </span>
                  </div>
                ))}
              </div>
              {acc.status !== "ARCHIVED" && (
                <div className="mt-2 grid grid-cols-12 gap-1.5 items-end rounded-xl bg-slate-50 p-3" data-testid="admin-acc-unit-add">
                  <label className="col-span-4 text-xs"><span className="text-slate-400 font-bold uppercase">Nome unità</span><input className="rounded-lg border border-slate-200 px-2 py-1.5 w-full text-sm" data-testid="admin-unit-name" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} /></label>
                  <label className="col-span-2 text-xs"><span className="text-slate-400 font-bold uppercase">Qtà</span><input type="number" className="rounded-lg border border-slate-200 px-2 py-1.5 w-full text-sm" data-testid="admin-unit-qty" value={nu.quantity} onChange={(e) => setNu({ ...nu, quantity: e.target.value })} /></label>
                  <label className="col-span-2 text-xs"><span className="text-slate-400 font-bold uppercase">Ospiti</span><input type="number" className="rounded-lg border border-slate-200 px-2 py-1.5 w-full text-sm" data-testid="admin-unit-cap" value={nu.capacity_total} onChange={(e) => setNu({ ...nu, capacity_total: e.target.value })} /></label>
                  <label className="col-span-3 text-xs"><span className="text-slate-400 font-bold uppercase">€/notte</span><input type="number" className="rounded-lg border border-slate-200 px-2 py-1.5 w-full text-sm" data-testid="admin-unit-price" value={nu.base_price} onChange={(e) => setNu({ ...nu, base_price: e.target.value })} /></label>
                  <button data-testid="admin-unit-add-btn" onClick={() => { if (!nu.name.trim()) return toast.error("Nome unità obbligatorio"); addUnit.mutate(); }} className="col-span-1 rounded-lg bg-primary text-white py-1.5 text-lg leading-none">+</button>
                </div>
              )}
            </div>

            <div>
              <h4 className="font-semibold text-slate-800 text-sm mb-2">Prenotazioni ({acc.bookings?.length || 0})</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-left"><tr>{["Numero", "Unità", "Check-in", "Check-out", "Ospiti", "Totale", "Stato"].map((h) => <th key={h} className="px-3 py-2 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {(acc.bookings || []).length === 0 ? <tr><td colSpan={7} className="px-3 py-4 text-center text-slate-400">Nessuna prenotazione.</td></tr> :
                      acc.bookings.map((b) => (
                        <tr key={b.id}>
                          <td className="px-3 py-2 font-semibold text-primary">{b.booking_number}</td>
                          <td className="px-3 py-2">{b.unit_name}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{b.check_in}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{b.check_out}</td>
                          <td className="px-3 py-2">{b.total_guests}</td>
                          <td className="px-3 py-2">{eur(b.gross_amount)}</td>
                          <td className="px-3 py-2 text-xs">{b.status}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AccCreateModal({ operators, onClose }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ operator_id: "", name: "", type: "bb", city: "", address: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const create = useMutation({
    mutationFn: () => api.adminCreateAccommodation({ name: f.name, type: f.type, city: f.city, address: f.address, operator_id: f.operator_id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminAcc"] }); toast.success("Struttura creata e pubblicata"); onClose(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const submit = () => {
    if (!f.operator_id) return toast.error("Seleziona l'operatore proprietario");
    if (!f.name.trim()) return toast.error("Inserisci il nome della struttura");
    create.mutate();
  };
  const ic = "rounded-lg border border-slate-200 px-3 py-2 text-sm w-full";
  const lb = "text-xs font-bold uppercase tracking-wide text-slate-400";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-acc-create-modal">
        <div className="flex items-center justify-between mb-4"><h3 className="font-heading font-semibold text-lg">Nuova struttura (Super Admin)</h3><button onClick={onClose} className="text-slate-400 text-2xl leading-none">×</button></div>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2"><span className={lb}>Operatore proprietario</span>
            <select data-testid="admin-acc-operator" className={ic} value={f.operator_id} onChange={(e) => set("operator_id", e.target.value)}>
              <option value="">— Seleziona operatore —</option>
              {(operators || []).map((o) => <option key={o.id} value={o.id}>{o.name || o.legal_name || o.id}</option>)}
            </select>
          </label>
          <label className="col-span-2"><span className={lb}>Nome struttura</span><input data-testid="admin-acc-name" className={ic} value={f.name} onChange={(e) => set("name", e.target.value)} /></label>
          <label><span className={lb}>Tipologia</span><select data-testid="admin-acc-type" className={ic} value={f.type} onChange={(e) => set("type", e.target.value)}>{Object.entries(TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
          <label><span className={lb}>Città</span><input data-testid="admin-acc-city" className={ic} value={f.city} onChange={(e) => set("city", e.target.value)} /></label>
          <label className="col-span-2"><span className={lb}>Indirizzo</span><input className={ic} value={f.address} onChange={(e) => set("address", e.target.value)} /></label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="rounded-full border px-5 py-2 text-sm font-semibold">Annulla</button>
          <button onClick={submit} disabled={create.isPending} data-testid="admin-acc-create-save" className="rounded-full bg-primary text-white px-6 py-2 text-sm font-semibold disabled:opacity-50">{create.isPending ? "Creazione…" : "Crea struttura"}</button>
        </div>
        <p className="text-[11px] text-slate-400 mt-3">Dopo la creazione apri il Dettaglio per aggiungere unità, prezzi e calendario.</p>
      </div>
    </div>
  );
}

export function AccommodationsAdminTab() {
  const { data: list = [], isLoading } = useQuery({ queryKey: ["adminAcc"], queryFn: api.adminAccommodations });
  const { data: operators = [] } = useQuery({ queryKey: ["adminOperators"], queryFn: api.adminOperators });
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  return (
    <div className="space-y-4" data-testid="admin-accommodations">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <p className="text-sm text-slate-500">Tutte le strutture ricettive. Crea una struttura per conto di un operatore, gestisci unità, stato e calendario.</p>
        <button onClick={() => setCreating(true)} data-testid="admin-new-accommodation-btn" className="rounded-full bg-primary text-white text-sm font-semibold px-5 py-2 whitespace-nowrap">+ Nuova struttura</button>
      </div>
      {isLoading ? <p className="text-slate-400">Caricamento...</p> : list.length === 0 ? (
        <p className="text-sm text-slate-400">Nessuna struttura registrata.</p>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>{["Struttura", "Tipo", "Operatore", "Città", "Unità", "Prenotazioni", "Stato", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50" data-testid={`admin-acc-row-${a.id}`}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{a.name}</td>
                  <td className="px-4 py-3">{TYPE_LABEL[a.type]}</td>
                  <td className="px-4 py-3">{a.operator_display}</td>
                  <td className="px-4 py-3">{a.city}</td>
                  <td className="px-4 py-3">{a.units_count}</td>
                  <td className="px-4 py-3">{a.bookings_count}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-bold ${a.status === "ACTIVE" ? "text-emerald-600" : a.status === "SUSPENDED" ? "text-red-500" : a.status === "ARCHIVED" ? "text-slate-500" : "text-amber-600"}`}>{a.status === "ARCHIVED" ? "ARCHIVIATA" : a.status}</span></td>
                  <td className="px-4 py-3"><button data-testid={`admin-acc-open-${a.id}`} onClick={() => setOpen(a.id)} className="text-xs font-semibold text-primary">Dettaglio</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && <AdminAccDetail accId={open} onClose={() => setOpen(null)} />}
      {creating && <AccCreateModal operators={operators} onClose={() => setCreating(false)} />}
    </div>
  );
}
